import { useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/data/auth";
import { useWorkspaceItems } from "@/data/store";
import { useSignInDialog } from "./SignInDialog";
import { Button } from "@/ui/Button";
import s from "./SignInBanner.module.css";

const LAST_DISMISS_KEY = "hn.nag.dismissedAt";
const MILESTONE_KEY = "hn.nag.milestone10";
const REAPPEAR_DAYS = 7;

/**
 * The careful, persistent, cancellable nag: a slim banner that shows on
 * first launch, again every ~7 days, and once more when the workspace
 * crosses 10 notes. Never a modal, never interrupts typing. The permanent
 * status-bar chip carries the message the rest of the time.
 */
export function SignInBanner() {
  const { status } = useAuth();
  const items = useWorkspaceItems();
  const openDialog = useSignInDialog((d) => d.setOpen);
  const [, force] = useState(0);

  if (status !== "signedOut") return null;

  const noteCount = items.filter((i) => i.type === "note" && !i.trashedAt && !i.archivedAt).length;
  const dismissedAt = Number(localStorage.getItem(LAST_DISMISS_KEY) ?? 0);
  const staleDismiss = Date.now() - dismissedAt > REAPPEAR_DAYS * 86400_000;
  const milestone = noteCount >= 10 && !localStorage.getItem(MILESTONE_KEY);

  if (!staleDismiss && !milestone) return null;

  const dismiss = () => {
    localStorage.setItem(LAST_DISMISS_KEY, String(Date.now()));
    if (milestone) localStorage.setItem(MILESTONE_KEY, "1");
    force((n) => n + 1);
  };

  return (
    <div className={s.banner} role="status">
      <span className={s.text}>
        {milestone
          ? `You have ${noteCount} notes that live only in this browser. Back them up free.`
          : "Your work is saved in this browser only. Sign in to sync it to the cloud, free."}
      </span>
      <Button size="sm" variant="solid" onClick={() => openDialog(true)}>
        Sign in
      </Button>
      <button type="button" className={s.dismiss} aria-label="Dismiss" onClick={dismiss}>
        <X size={14} />
      </button>
    </div>
  );
}
